import React from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateField } from "@mui/x-date-pickers";
import axiosInstance from "../../../../hooks/api";
import { useFormSubmit } from "../../../../hooks/useFormSubmit";
import CopyFromPanel from "./CopyFromPanel";
import { CHILD_COPY_SECTIONS } from "../../../../helper/copyFormSections";

const SACRAMENT_ORDER = ['baptism', 'communion', 'confirmation', 'matrimony'];

const RECEIVED_OPTIONS = [
  { id: 'baptism',      key: 'has_baptism',      label: '_tab_baptism' },
  { id: 'communion',    key: 'has_communion',    label: '_tab_communion' },
  { id: 'confirmation', key: 'has_confirmation', label: '_tab_confirmation' },
];

const SectionHeader = ({ title }) => (
  <h6 style={{ borderBottom: "2px solid #032C65", paddingBottom: "6px", marginBottom: "16px", color: "#032C65", marginTop: "24px" }}>
    {title}
  </h6>
);

const DateInput = ({ name, required, control, error }) => {
  const { t } = useTranslation();
  return (
    <Controller
      control={control}
      name={name}
      rules={required ? { required: t("_err_required") } : {}}
      render={({ field }) => (
        <>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateField
              sx={{ width: "100%" }}
              onChange={(v) => { if (v?.isValid()) field.onChange(v.format("YYYY-MM-DD")); }}
            />
          </LocalizationProvider>
          {error && <div className="invalid-feedback d-block">{error.message}</div>}
        </>
      )}
    />
  );
};

const NasChildrenRegForm = ({
  sacramentType,
  birthDate,
  age,
  onSuccess,
  multiFlow = false,
  isFinalStep = false,
  onContinue,
  onSubmitBatch,
  isBatchLoading = false,
  copySources = [],
}) => {
  const { t } = useTranslation();
  const { isLoading, submit } = useFormSubmit();
  const { register, handleSubmit, watch, reset, control, setValue, formState: { errors } } = useForm({ mode: "onTouched" });

  const livesWithValue   = watch("lives_with");
  const courtOrderValue  = watch("has_court_order");
  const parentsSeparated = watch("parents_separated");

  const idx = SACRAMENT_ORDER.indexOf(sacramentType);
  const sacramentsBefore = idx >= 0 ? SACRAMENT_ORDER.slice(0, idx) : [];
  const busy = isLoading || isBatchLoading;

  const onSubmit = async (data) => {
    if (multiFlow) {
      const draft = { kind: "child", sacramentType, birthDate, age, data };
      if (!isFinalStep) {
        if (onContinue) onContinue(draft);
        return;
      }
      if (onSubmitBatch) await onSubmitBatch(draft);
      return;
    }

    const payload = new FormData();
    payload.append("sacrament_type", sacramentType);
    payload.append("form_type", "child");
    payload.append("full_name", data.child_name);
    payload.append("birth_date", birthDate);
    payload.append("phone", data.phone);
    payload.append("email", data.mother_email || data.father_email || "");
    payload.append("address_line_1", data.address_line_1);
    payload.append("city", data.city);
    payload.append("state", data.state);
    payload.append("zip", data.zip);
    payload.append("language", data.child_language);
    const { documents: files, ...rest } = data;
    payload.append("form_data", JSON.stringify({ ...rest, age, birth_date: birthDate }));
    if (files && files.length > 0) {
      Array.from(files).forEach((f) => payload.append("documents[]", f));
    }

    await submit(
      () => axiosInstance.post("/public/sacrament-registrations", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
      {
        successText: t("_registration_confirmation"),
        onSuccess: () => {
          reset();
          if (onSuccess) onSuccess();
        },
      }
    );
  };

  const submitLabel = multiFlow
    ? (isFinalStep ? t("_submit_all_registrations") : t("_continue_next_person"))
    : t("_submit_registration");


  return (
    <form onSubmit={handleSubmit(onSubmit)} className="sigma_contact-form p-4" style={{ background: "#fff", borderRadius: "8px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>

      {multiFlow && (
        <CopyFromPanel
          sources={copySources}
          sections={CHILD_COPY_SECTIONS}
          setValue={setValue}
        />
      )}

      <div
        style={{
          background: "#e8f0fb",
          padding: "14px",
          borderRadius: "6px",
          marginBottom: "16px",
          fontSize: "13px",
          fontWeight: 600,
          color: "#032C65",
          whiteSpace: "pre-line",
        }}
      >
        {t("_fee_re_and_year_end_note")}
      </div>

      {/* ── Datos del Niño/a ── */}
      <SectionHeader title={t("_section_child_info")} />
      <div className="row g-3 mb-2">
        <div className="col-sm-8">
          <label className="form-label fw-semibold">{t("_child_name")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.child_name ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("child_name", { required: t("_err_required") })}
          />
          {errors.child_name && <div className="invalid-feedback">{errors.child_name.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_sex")} <em className="text-danger">*</em></label>
          <select
            className={`form-select border ${errors.sex ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("sex", {
              required: t("_err_required"),
              validate: (v) => v !== "" || t("_err_select_option"),
            })}
          >
            <option value="">—</option>
            <option value="F">F</option>
            <option value="M">M</option>
          </select>
          {errors.sex && <div className="invalid-feedback">{errors.sex.message}</div>}
        </div>
        <div className="col-sm-8">
          <label className="form-label fw-semibold">{t("_address_1")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.address_line_1 ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("address_line_1", { required: t("_err_required") })}
          />
          {errors.address_line_1 && <div className="invalid-feedback">{errors.address_line_1.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_apt")}</label>
          <input className="form-control border" {...register("apt")} />
        </div>
        <div className="col-sm-5">
          <label className="form-label fw-semibold">{t("_city")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.city ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("city", { required: t("_err_required") })}
          />
          {errors.city && <div className="invalid-feedback">{errors.city.message}</div>}
        </div>
        <div className="col-sm-3">
          <label className="form-label fw-semibold">{t("_state")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.state ? "is-invalid" : ""}`}
            maxLength={2}
            style={{ textTransform: "uppercase", scrollMarginTop: "100px" }}
            {...register("state", { required: t("_err_required") })}
          />
          {errors.state && <div className="invalid-feedback">{errors.state.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_zip")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.zip ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("zip", { required: t("_err_required") })}
          />
          {errors.zip && <div className="invalid-feedback">{errors.zip.message}</div>}
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_phone")} <em className="text-danger">*</em></label>
          <input
            type="tel"
            className={`form-control border ${errors.phone ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("phone", { required: t("_err_required") })}
          />
          {errors.phone && <div className="invalid-feedback">{errors.phone.message}</div>}
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_birth_place")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.birth_place ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("birth_place", { required: t("_err_required") })}
          />
          {errors.birth_place && <div className="invalid-feedback">{errors.birth_place.message}</div>}
        </div>
        <div className="col-sm-8">
          <label className="form-label fw-semibold">{t("_school")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.school ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("school", { required: t("_err_required") })}
          />
          {errors.school && <div className="invalid-feedback">{errors.school.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_grade")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.grade ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("grade", { required: t("_err_required") })}
          />
          {errors.grade && <div className="invalid-feedback">{errors.grade.message}</div>}
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_child_language")} <em className="text-danger">*</em></label>
          <div>
            <label className="me-3"><input type="radio" value="es" style={{ scrollMarginTop: "100px" }} {...register("child_language", { required: t("_err_required") })} /> {t("_spanish")}</label>
            <label><input type="radio" value="en" {...register("child_language")} /> {t("_english")}</label>
          </div>
          {errors.child_language && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.child_language.message}</div>}
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_parent_language")} <em className="text-danger">*</em></label>
          <div>
            <label className="me-3"><input type="radio" value="es" style={{ scrollMarginTop: "100px" }} {...register("parent_language", { required: t("_err_required") })} /> {t("_spanish")}</label>
            <label><input type="radio" value="en" {...register("parent_language")} /> {t("_english")}</label>
          </div>
          {errors.parent_language && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.parent_language.message}</div>}
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_child_lives_with")} <em className="text-danger">*</em></label>
          <select
            className={`form-select border ${errors.lives_with ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("lives_with", {
              required: t("_err_required"),
              validate: (v) => v !== "" || t("_err_select_option"),
            })}
          >
            <option value="">—</option>
            <option value="both_parents">{t("_both_parents")}</option>
            <option value="mother">{t("_mother")}</option>
            <option value="father">{t("_father")}</option>
            <option value="guardian">{t("_guardian")}</option>
            <option value="legal_tutor">{t("_legal_tutor")}</option>
            <option value="other">{t("_other")}</option>
          </select>
          {errors.lives_with && <div className="invalid-feedback">{errors.lives_with.message}</div>}
        </div>
        {livesWithValue === "other" && (
          <div className="col-sm-6">
            <label className="form-label fw-semibold">{t("_lives_with_other_specify")} <em className="text-danger">*</em></label>
            <input
              type="text"
              className={`form-control border ${errors.lives_with_other ? "is-invalid" : ""}`}
              style={{ scrollMarginTop: "100px" }}
              {...register("lives_with_other", { required: t("_err_required") })}
            />
            {errors.lives_with_other && <div className="invalid-feedback">{errors.lives_with_other.message}</div>}
          </div>
        )}
        {age > 12 && (
          <div className="col-sm-12">
            <label className="form-label fw-semibold">{t("_can_go_home_alone")} <em className="text-danger">*</em></label>
            <div>
              <label className="me-3"><input type="radio" value="yes" style={{ scrollMarginTop: "100px" }} {...register("can_go_home_alone", { required: t("_err_required") })} /> {t("_yes")}</label>
              <label><input type="radio" value="no" {...register("can_go_home_alone")} /> {t("_no")}</label>
            </div>
            {errors.can_go_home_alone && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.can_go_home_alone.message}</div>}
          </div>
        )}
      </div>

      {/* ── Información de Madre ── */}
      <SectionHeader title={t("_section_mothers_info")} />
      <div className="row g-3 mb-2">
        <div className="col-sm-12">
          <label className="form-label fw-semibold">{t("_mother_name")}</label>
          <input className="form-control border" {...register("mother_name")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_mother_phone")}</label>
          <input type="tel" className="form-control border" {...register("mother_phone")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_mother_email")}</label>
          <input type="email" className="form-control border" {...register("mother_email")} />
        </div>
        <div className="col-sm-12">
          <label className="form-label fw-semibold">{t("_mother_catholic")}</label>
          <div>
            <label className="me-3"><input type="radio" value="yes" {...register("mother_catholic")} /> {t("_yes")}</label>
            <label><input type="radio" value="no" {...register("mother_catholic")} /> {t("_no")}</label>
          </div>
        </div>
      </div>

      {/* ── Información del Padre ── */}
      <SectionHeader title={t("_section_fathers_info")} />
      <div className="row g-3 mb-2">
        <div className="col-sm-12">
          <label className="form-label fw-semibold">{t("_father_name")}</label>
          <input className="form-control border" {...register("father_name")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_father_phone")}</label>
          <input type="tel" className="form-control border" {...register("father_phone")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_father_email")}</label>
          <input type="email" className="form-control border" {...register("father_email")} />
        </div>
        <div className="col-sm-12">
          <label className="form-label fw-semibold">{t("_father_catholic")}</label>
          <div>
            <label className="me-3"><input type="radio" value="yes" {...register("father_catholic")} /> {t("_yes")}</label>
            <label><input type="radio" value="no" {...register("father_catholic")} /> {t("_no")}</label>
          </div>
        </div>
      </div>

      {/* ── Guardián ── */}
      <SectionHeader title={t("_section_guardian")} />
      <div className="row g-3 mb-2">
        <div className="col-sm-12">
          <label className="form-label fw-semibold">{t("_guardian_name")}</label>
          <input className="form-control border" {...register("guardian_name")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_guardian_phone")}</label>
          <input type="tel" className="form-control border" {...register("guardian_phone")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_guardian_email")}</label>
          <input type="email" className="form-control border" {...register("guardian_email")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_guardian_catholic")}</label>
          <div>
            <label className="me-3"><input type="radio" value="yes" {...register("guardian_catholic")} /> {t("_yes")}</label>
            <label><input type="radio" value="no" {...register("guardian_catholic")} /> {t("_no")}</label>
          </div>
        </div>
        <div className="col-sm-12">
          <label className="form-label fw-semibold">{t("_parents_separated")}</label>
          <div>
            <label className="me-3"><input type="radio" value="yes" {...register("parents_separated")} /> {t("_yes")}</label>
            <label><input type="radio" value="no" {...register("parents_separated")} /> {t("_no")}</label>
          </div>
        </div>
        {parentsSeparated === "yes" && (
          <>
            <div className="col-sm-12">
              <label className="form-label fw-semibold">{t("_other_parent_legal_access")} <em className="text-danger">*</em></label>
              <div>
                <label className="me-3"><input type="radio" value="yes" style={{ scrollMarginTop: "100px" }} {...register("other_parent_legal_access", { required: t("_err_required") })} /> {t("_yes")}</label>
                <label><input type="radio" value="no" {...register("other_parent_legal_access")} /> {t("_no")}</label>
              </div>
              {errors.other_parent_legal_access && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.other_parent_legal_access.message}</div>}
            </div>
            <div className="col-sm-12">
              <label className="form-label fw-semibold">{t("_has_court_order")}</label>
              <div>
                <label className="me-3"><input type="radio" value="yes" {...register("has_court_order")} /> {t("_yes")}</label>
                <label><input type="radio" value="no" {...register("has_court_order")} /> {t("_no")}</label>
              </div>
            </div>
            {courtOrderValue === "yes" && (
              <div className="col-sm-12">
                <label className="form-label fw-semibold">{t("_court_order_details")} <em className="text-danger">*</em></label>
                <textarea
                  className={`form-control border ${errors.court_order_details ? "is-invalid" : ""}`}
                  rows={2}
                  style={{ scrollMarginTop: "100px" }}
                  {...register("court_order_details", { required: t("_err_required") })}
                />
                {errors.court_order_details && <div className="invalid-feedback">{errors.court_order_details.message}</div>}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Antecedentes Médicos ── */}
      <SectionHeader title={t("_section_medical")} />
      <div className="row g-3 mb-2">
        {[
          { key: "has_medical_issues", textKey: "medical_issues_details", labelKey: "_has_medical_issues", detailLabelKey: "_specify" },
          { key: "takes_medication",   textKey: "medication_details",     labelKey: "_takes_medication",   detailLabelKey: "_specify" },
          { key: "has_epipen",         textKey: "epipen_details",         labelKey: "_has_epipen",         detailLabelKey: "_specify" },
          { key: "has_allergies",      textKey: "allergy_details",        labelKey: "_has_allergies",      detailLabelKey: "_specify" },
          { key: "has_learning_disability", textKey: "learning_disability_details", labelKey: "_has_learning_disability", detailLabelKey: "_explain" },
        ].map(({ key, textKey, labelKey, detailLabelKey }) => {
          const val = watch(key);
          return (
            <div key={key} className="col-sm-12">
              <label className="form-label fw-semibold">{t(labelKey)}</label>
              <div className="mb-1">
                <label className="me-3"><input type="radio" value="yes" {...register(key)} /> {t("_yes")}</label>
                <label><input type="radio" value="no" {...register(key)} /> {t("_no")}</label>
              </div>
              {val === "yes" && (
                <>
                  <input
                    type="text"
                    className={`form-control border ${errors[textKey] ? "is-invalid" : ""}`}
                    placeholder={t(detailLabelKey)}
                    style={{ scrollMarginTop: "100px" }}
                    {...register(textKey, { required: t("_err_required") })}
                  />
                  {errors[textKey] && <div className="invalid-feedback">{errors[textKey].message}</div>}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Educación Religiosa Anterior ── */}
      <SectionHeader title={t("_section_religious_history")} />
      <div className="row g-3 mb-2">
        <div className="col-sm-12">
          <label className="form-label fw-semibold">{t("_enrolled_last_year")}</label>
          <div>
            <label className="me-3"><input type="radio" value="yes" {...register("enrolled_last_year")} /> {t("_yes")}</label>
            <label><input type="radio" value="no" {...register("enrolled_last_year")} /> {t("_no")}</label>
          </div>
        </div>
        <div className="col-sm-12">
          <label className="form-label fw-semibold">{t("_prev_parish_education")}</label>
          <div>
            <label className="me-3"><input type="radio" value="yes" {...register("prev_parish_education")} /> {t("_yes")}</label>
            <label><input type="radio" value="no" {...register("prev_parish_education")} /> {t("_no")}</label>
          </div>
        </div>
        {watch("prev_parish_education") === "yes" && (
          <>
            <div className="col-sm-6">
              <label className="form-label fw-semibold">{t("_prev_year")} <em className="text-danger">*</em></label>
              <input
                type="text"
                className={`form-control border ${errors.prev_year ? "is-invalid" : ""}`}
                style={{ scrollMarginTop: "100px" }}
                {...register("prev_year", { required: t("_err_required") })}
              />
              {errors.prev_year && <div className="invalid-feedback">{errors.prev_year.message}</div>}
            </div>
            <div className="col-sm-6">
              <label className="form-label fw-semibold">{t("_prev_parish_name")} <em className="text-danger">*</em></label>
              <input
                type="text"
                className={`form-control border ${errors.prev_parish_name ? "is-invalid" : ""}`}
                style={{ scrollMarginTop: "100px" }}
                {...register("prev_parish_name", { required: t("_err_required") })}
              />
              {errors.prev_parish_name && <div className="invalid-feedback">{errors.prev_parish_name.message}</div>}
            </div>
          </>
        )}
        {sacramentsBefore.length > 0 && (
          <div className="col-sm-12">
            <label className="form-label fw-semibold">{t("_sacraments_received")} <small className="text-muted">({t("_cert_required")})</small></label>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              {RECEIVED_OPTIONS.filter(s => sacramentsBefore.includes(s.id)).map(s => (
                <label key={s.id}><input type="checkbox" {...register(s.key)} /> {t(s.label)}</label>
              ))}
            </div>
          </div>
        )}
      </div>

      {sacramentType === "confirmation" && (
        <>
          <SectionHeader title={t("_confirmation_sponsor")} />
          <p className="text-muted" style={{ fontSize: "12.5px", marginTop: "-8px" }}>{t("_confirmation_sponsor_note")}</p>
          <div className="row g-3 mb-2">
            <div className="col-sm-6">
              <label className="form-label fw-semibold">{t("_godparents_full_name")} <em className="text-danger">*</em></label>
              <input
                type="text"
                className={`form-control border ${errors.sponsor_name ? "is-invalid" : ""}`}
                style={{ scrollMarginTop: "100px" }}
                {...register("sponsor_name", { required: t("_err_required") })}
              />
              {errors.sponsor_name && <div className="invalid-feedback">{errors.sponsor_name.message}</div>}
            </div>
            <div className="col-sm-6">
              <label className="form-label fw-semibold">{t("_sponsor_religion")}</label>
              <input className="form-control border" {...register("sponsor_religion")} />
            </div>
            <div className="col-sm-6">
              <label className="form-label fw-semibold">{t("_phone")}</label>
              <input type="tel" className="form-control border" {...register("sponsor_phone")} />
            </div>
            <div className="col-sm-6">
              <label className="form-label fw-semibold">{t("_email")}</label>
              <input
                type="email"
                className={`form-control border ${errors.sponsor_email ? "is-invalid" : ""}`}
                {...register("sponsor_email", {
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t("_err_invalid_email") },
                })}
              />
              {errors.sponsor_email && <div className="invalid-feedback">{errors.sponsor_email.message}</div>}
            </div>
          </div>
        </>
      )}

      {/* ── Firma Principal ── */}
      <SectionHeader title={t("_section_signature")} />
      <div className="row g-3 mb-2">
        <div className="col-sm-8">
          <label className="form-label fw-semibold">{t("_guardian_signature")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.signature ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            placeholder={t("_type_full_name")}
            {...register("signature", { required: t("_err_required") })}
          />
          {errors.signature && <div className="invalid-feedback">{errors.signature.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_date")} <em className="text-danger">*</em></label>
          <DateInput name="signature_date" required control={control} error={errors.signature_date} />
        </div>
      </div>

      {/* ── Contactos de Emergencia ── */}
      <SectionHeader title={t("_section_emergency_contacts")} />
      <div className="row g-3 mb-2">
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_contact1_name")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.emergency1_name ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("emergency1_name", { required: t("_err_required") })}
          />
          {errors.emergency1_name && <div className="invalid-feedback">{errors.emergency1_name.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_relation")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.emergency1_relation ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("emergency1_relation", { required: t("_err_required") })}
          />
          {errors.emergency1_relation && <div className="invalid-feedback">{errors.emergency1_relation.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_phone")} <em className="text-danger">*</em></label>
          <input
            type="tel"
            className={`form-control border ${errors.emergency1_phone ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("emergency1_phone", { required: t("_err_required") })}
          />
          {errors.emergency1_phone && <div className="invalid-feedback">{errors.emergency1_phone.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_contact2_name")}</label>
          <input className="form-control border" {...register("emergency2_name")} />
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_relation")}</label>
          <input className="form-control border" {...register("emergency2_relation")} />
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_phone")}</label>
          <input type="tel" className="form-control border" {...register("emergency2_phone")} />
        </div>
        <div className="col-sm-12">
          <label style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
            <input
              type="checkbox"
              style={{ marginTop: "3px", scrollMarginTop: "100px" }}
              {...register("emergency_auth", { required: t("_err_required") })}
            />
            <span>{t("_emergency_treatment_auth")}</span>
          </label>
          {errors.emergency_auth && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.emergency_auth.message}</div>}
        </div>
        <div className="col-sm-8">
          <label className="form-label fw-semibold">{t("_guardian_signature")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.emergency_signature ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            placeholder={t("_type_full_name")}
            {...register("emergency_signature", { required: t("_err_required") })}
          />
          {errors.emergency_signature && <div className="invalid-feedback">{errors.emergency_signature.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_date")} <em className="text-danger">*</em></label>
          <DateInput name="emergency_signature_date" required control={control} error={errors.emergency_signature_date} />
        </div>
      </div>

      {/* ── Consentimiento de Fotos ── */}
      <SectionHeader title={t("_section_photo_consent")} />
      <div className="mb-3" style={{ background: "#f8f9fa", padding: "12px", borderRadius: "6px", fontSize: "13px", color: "#555" }}>
        {t("_photo_consent_text")}
      </div>
      <div className="row g-3 mb-2">
        <div className="col-sm-12">
          <label className="me-4">
            <input type="radio" value="yes" style={{ scrollMarginTop: "100px" }} {...register("photo_consent", { required: t("_err_required") })} /> {t("_photo_consent_yes")}
          </label>
          <label>
            <input type="radio" value="no" {...register("photo_consent")} /> {t("_photo_consent_no")}
          </label>
          {errors.photo_consent && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.photo_consent.message}</div>}
        </div>
        <div className="col-sm-8">
          <label className="form-label fw-semibold">{t("_guardian_signature")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.photo_signature ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            placeholder={t("_type_full_name")}
            {...register("photo_signature", { required: t("_err_required") })}
          />
          {errors.photo_signature && <div className="invalid-feedback">{errors.photo_signature.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_date")} <em className="text-danger">*</em></label>
          <DateInput name="photo_signature_date" required control={control} error={errors.photo_signature_date} />
        </div>
      </div>

      {/* ── Donación Informativa ── */}
      <SectionHeader title={t("_section_fee")} />
      <div style={{ background: "#e8f0fb", padding: "14px", borderRadius: "6px", marginBottom: "16px", fontSize: "13px" }}>
        <div><strong>$60</strong> — 1 {t("_child")}</div>
        <div><strong>$100</strong> — 2 {t("_children")}</div>
        <div><strong>$120</strong> — 3 {t("_children")}</div>
        <div><strong>$140</strong> — 4 {t("_children")}</div>
        <hr style={{ margin: "8px 0" }} />
        <div className="text-muted">{t("_fee_siblings_note")}</div>
        <div className="text-muted">{t("_fee_nonrefundable")}</div>
        <div style={{ fontWeight: 600, color: "#032C65", whiteSpace: "pre-line" }}>
          {t("_fee_re_and_year_end_note")}
        </div>
      </div>

      {/* ── Documentos ── */}
      <SectionHeader title={t("_section_documents")} />
      <div className="mb-4">
        <label className="form-label fw-semibold">{t("_attach_documents")}</label>
        <input
          type="file"
          className="form-control border"
          multiple
          accept="application/pdf,image/*"
          {...register("documents")}
        />
        <small className="text-muted">{t("_accepted_formats")}: PDF, JPG, PNG. Max 10MB {t("_per_file")}.</small>
      </div>

      {/* ── Comentarios ── */}
      <div className="mb-4">
        <label className="form-label fw-semibold">{t("_comments")}</label>
        <textarea className="form-control border" rows={3} {...register("comments")} />
      </div>

      {/* ── Submit ── */}
      <div className="d-grid">
        <button
          type="submit"
          disabled={busy}
          className={busy ? "sigma_btn-custom" : "sigma_btn-custom light"}
          style={{ backgroundColor: busy ? "grey" : "", padding: "14px", fontSize: "16px" }}
        >
          {busy ? "..." : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default NasChildrenRegForm;
